import {
  createNewMessage,
  createNewQuery,
  deleteQuery,
  getClientDocuments,
  getQueryMessages,
  getUserQueries,
  updateMessageResponse,
  updateQuery,
} from '../../services/axios';
import {
  CLEAN_QUERIES_REDUCER,
  CLEAR_QUERIES,
  CREATE_NEW_MESSAGE,
  CREATE_NEW_QUERY,
  DELETE_QUERY,
  DELETE_QUERY_ERROR,
  GET_AI_RESPONSE,
  GET_QUERIES,
  SET_QUERIES_LOADING,
  UPDATE_AI_RESPONSE,
  UPDATE_QUERIES,
  UPDATE_QUERY_TYPE,
} from './revealrTypes';

const newDateToday = new Date();

const today = newDateToday.toLocaleDateString([], {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

newDateToday.setDate(newDateToday.getDate() - 7);
const previous7 = newDateToday.toLocaleDateString([], {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const areDatesEqual = (date1, date2) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const isPrevious7Days = (queryDate, todayDate, previous7Date) => {
  // Check if the query is not from today but was created in the previous 7 days
  return !areDatesEqual(todayDate, queryDate) && queryDate >= previous7Date;
};

export const CleanQueriesReducer = () => {
  return (dispatch) => {
    dispatch({
      type: CLEAN_QUERIES_REDUCER,
    });
  };
};

export const setLoading = () => ({
  type: SET_QUERIES_LOADING,
});

const verifyDocIds = (docIds, documents) => {
  // Create a Set from the document IDs for efficient lookup
  const docIdSet = new Set(documents.map((doc) => doc.id));

  // Check if every docId in the selectedQuery's docIds exists in the Set
  return docIds.every((docId) => docIdSet.has(docId));
};

export const GetUserQueries = () => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const { token, userID, spKey } = getState().user.user;
    let docsList = getState().client.documents;

    if (!docsList || docsList === null)
      docsList = await getClientDocuments(spKey);

    const queries = await getUserQueries(token, userID);

    if (queries && queries.length > 0) {
      const processedQueries = await Promise.all(
        queries.map(async (query) => {
          const messages = await getQueryMessages(token, query.id);

          const newDate =
            query && query.created
              ? new Date(query.created).toLocaleDateString([], {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })
              : '';

          const queryDate = new Date(query.created);
          const todayDate = new Date(today);
          const previous7Date = new Date(previous7);

          const isToday = areDatesEqual(todayDate, queryDate);
          const isPrevious7 = isPrevious7Days(
            queryDate,
            todayDate,
            previous7Date
          );

          return {
            id: query.id,
            created: newDate,
            title: query.topic,
            queryDay: isToday
              ? 'Today'
              : isPrevious7
              ? 'Previous7'
              : 'Previous',
            docIds: query.documentId ? query.documentId.split(',') : '',
            docIdsExist:
              query.documentId && query.documentId.length > 0
                ? verifyDocIds(query.documentId.split(','), docsList)
                : true,
            queryContent:
              messages && messages.length > 0
                ? messages
                    .filter((message) => query.id === message.queryId)
                    .map((item) => {
                      return {
                        ...item,
                        response: JSON.parse(item.response),
                      };
                    })
                    .sort((a, b) =>
                      new Date(a.created) < new Date(b.created) ? -1 : 1
                    )
                : '',
            isFirstTime:
              messages.filter((message) => query.id === message.queryId)
                .length > 0
                ? false
                : true,
          };
        })
      );

      dispatch({
        type: GET_QUERIES,
        payload: processedQueries.sort((a, b) => (a.id > b.id ? -1 : 1)),
      });
      return processedQueries.sort((a, b) => (a.id > b.id ? -1 : 1));
    } else {
      dispatch({
        type: GET_QUERIES,
        payload: [],
      });
      return [];
    }
  };
};

export const CreateNewQuery = (startedId) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const { token, userID } = getState().user.user;
    // const { queries } = getState().queries;

    const data = {
      userId: userID,
      documentId: startedId ? startedId : '',
      // documentId: startedId,
      topic: '',
    };

    const response = await createNewQuery(token, data);

    if (response && response.success) {
      dispatch({
        type: CREATE_NEW_QUERY,
      });
      return response && response.success;
    } else {
      dispatch({
        type: CLEAR_QUERIES,
      });
      return false;
    }
  };
};

export const DeleteQuery = (query) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const { token } = getState().user.user;
    const deleteRes = await deleteQuery(token, query.id);

    if (deleteRes && deleteRes.success) {
      dispatch({
        type: DELETE_QUERY,
      });
      return true;
    } else {
      console.log('Could not delete messages', query.id);
      dispatch({
        type: DELETE_QUERY_ERROR,
      });
      return false;
    }
    // } else console.log('Could not delete chat', query.sharepointId);
  };
};

export const CreateNewMessage = (queryId, message) => {
  return async (dispatch, getState) => {
    // dispatch(setLoading());

    const { token, userID } = getState().user.user;
    const queries = getState().queries.queries;

    const data = {
      queryId: queryId,
      userId: userID,
      message: message,
    };
    const response = await createNewMessage(token, data);
    // console.log('createNewMessage', response);

    if (response && response.success) {
      const newQuery = queries.filter((query) => query.id === queryId)[0];
      // console.log('newQuery', newQuery);

      const updatedQuery = {
        ...newQuery,
        queryContent: [...newQuery.queryContent, response.success],
      };
      // console.log('updatedQuery', updatedQuery);

      dispatch({
        type: CREATE_NEW_MESSAGE,
        payload: [
          ...queries.filter((query) => query.id !== queryId),
          updatedQuery,
        ],
      });
      // const resJson = await response.json();
      return response;
    } else {
      console.log('Error creating new message.');
      return null;
    }
  };
};

export const UpdateQuery = (queryId, queryTitle, docIds) => {
  return async (dispatch, getState) => {
    // dispatch(setLoading());

    const { token, userID } = getState().user.user;

    try {
      const data = {
        id: queryId,
        userId: userID,
        topic: queryTitle,
        documentId: docIds.toString(),
      };

      const response = await updateQuery(token, data);

      dispatch({
        type: UPDATE_QUERY_TYPE,
      });

      return response && response.success;
    } catch (error) {
      console.log('Update query ', error);
    }
  };
};

export const UpdateQueryTitle = (queryId, queryTitle) => {
  return async (dispatch, getState) => {
    try {
      // get queries from state
      const queries = getState().queries.queries;

      // update the queries state
      const newQueries = [];

      queries.map((query) => {
        if (query.id === queryId) {
          newQueries.push({
            ...query,
            title: queryTitle,
          });
        } else {
          newQueries.push(query);
        }
      });

      dispatch({
        type: UPDATE_QUERIES,
        payload: newQueries,
      });
      return true;
    } catch (e) {
      console.log('Error updating page', e);
    }
  };
};

export const GetAiResponse = (
  query,
  documentIds,
  chatId,
  startedDocId,
  history,
  needsTitle
) => {
  return async (dispatch, getState) => {
    const URL =
      'https://1wf-au-nlp-api.azurewebsites.net/revealrai/generatequery';

    const { userID } = getState().user.user;
    const siteName = 'RegulationCity';

    const headers = {
      Accept: '*',
      'Content-type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };

    const body = {
      query: query,
      site_name: siteName,
      client_name: 'RegulationCity',
      document_ids: documentIds,
      conversational_memory: history,
      active_document: startedDocId,
      user_id: userID,
      chat_id: chatId,
      needs_title: needsTitle,
    };

    try {
      const res = await fetch(URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
      });

      if (res && res.ok) {
        const response = await res.json();

        dispatch({
          type: GET_AI_RESPONSE,
        });

        // The API wraps the payload in an envelope ({ status, result, ... }).
        // Unwrap `result` so callers keep receiving { response, source_documents, title }.
        return response && response.result ? response.result : response;
      } else {
        console.log('Error getting AI response.');
        return null;
      }
    } catch (error) {
      console.log('Error getting AI response, ', error);
      return null;
    }
  };
};

export const UpdateResponse = (messageId, response) => {
  return async (dispatch, getState) => {
    // dispatch(setLoading());

    const { token } = getState().user.user;

    try {
      const data = {
        id: messageId,
        response: response,
      };

      const res = await updateMessageResponse(token, messageId, data);

      if (res && res.success) {
        dispatch({
          type: UPDATE_AI_RESPONSE,
        });

        return res && res.success;
      }
    } catch (error) {
      console.log('Update AI response ', error);
      return null;
    }
  };
};

export const UpdateQueryUserMessage = (query) => {
  return async (dispatch, getState) => {
    try {
      // get queries from state
      const queries = getState().queries.queries;

      const newQueries = [];

      queries.map((item) => {
        if (query.id === item.id) {
          newQueries.push(query);
        } else newQueries.push(item);
      });

      dispatch({
        type: UPDATE_QUERIES,
        payload: newQueries,
      });
      return true;
    } catch (e) {
      console.log('Error updating queries', e);
    }
  };
};
